import React, { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { editorTheme, floatingSlabSx, layerPalette } from '../utils/editor-theme';
import type { EditableFloorPlan } from '../models/editor-feature';
import type { LayerVisibility } from '../utils/layer-settings';
import { buildDrawPlan } from '../utils/draw-plan';
import { getMdiIconPath } from '../utils/mdi-icons';
import { elementToSurface, paint, surfaceTransformOf } from '../utils/paint';
import { computeBoundingBox, createProjection } from '../utils/projection';
import type { ViewBox } from '../hooks/use-viewport';

interface Props {
  readonly floorPlan: EditableFloorPlan;
  /** The editor surface's own coordinate space, which the minimap shows all of. */
  readonly surfaceWidth: number;
  readonly surfaceHeight: number;
  readonly viewBox: ViewBox;
  readonly layerVisibility: LayerVisibility;
  readonly onCentreOn: (x: number, y: number) => void;
  readonly width?: number;
  readonly height?: number;
}

/**
 * The whole plan, and where in it the editor is looking.
 *
 * Earns its keep the moment somebody zooms in to draw a wall: at 8× on a floor plate the
 * canvas shows one room and nothing says which. Fit-to-view answers that but throws the zoom
 * away, so the admin zooms out to orient themselves and back in to keep working, over and
 * over. This answers it without moving the view at all, and clicking it moves the view
 * without changing the zoom — the two halves of the same problem.
 *
 * Draws the plan itself rather than a coloured block per layer: the shapes are what make a
 * floor recognisable, and a minimap you cannot recognise your own building in is decoration.
 */
const Minimap = ({
  floorPlan,
  surfaceWidth,
  surfaceHeight,
  viewBox,
  layerVisibility,
  onCentreOn,
  width = 200,
  height = 134,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const projection = useMemo(
    () => createProjection(computeBoundingBox(floorPlan), surfaceWidth, surfaceHeight),
    [floorPlan, surfaceWidth, surfaceHeight],
  );

  const fullView = useMemo<ViewBox>(
    () => ({ x: 0, y: 0, width: surfaceWidth, height: surfaceHeight }),
    [surfaceWidth, surfaceHeight],
  );

  const commands = useMemo(
    () => buildDrawPlan(floorPlan, {
      showFloor: layerVisibility.floor,
      showRooms: layerVisibility.rooms,
      showBorder: layerVisibility.border,
      showDesks: layerVisibility.desks,
      showDeskShapes: layerVisibility.deskShapes,
      // Labels are unreadable at this size and only add noise; the shapes are what make the
      // floor recognisable.
      showLabels: false,
      showRetiredDesks: layerVisibility.retiredDesks,
      // No selection and no hover: this is an overview, and highlighting on it would be
      // invisible at two hundred pixels wide.
      selectedKeys: new Set<string>(),
      iconPathOf: getMdiIconPath,
      iconsLoaded: false,
      planScale: projection.scale,
      unitsPerMetre: floorPlan.unitsPerMetre ?? null,
    }),
    [floorPlan, layerVisibility, projection.scale],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context) {
      return undefined;
    }

    const ratio = window.devicePixelRatio || 1;

    if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
    }

    const frame = requestAnimationFrame(() => {
      paint(context, {
        commands,
        projection,
        viewBox: fullView,
        width,
        height,
        devicePixelRatio: ratio,
      });

      // The viewport rectangle, drawn in element pixels rather than surface units so its
      // outline stays a hairline however far the editor is zoomed in.
      const transform = surfaceTransformOf(width, height, fullView);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.strokeStyle = layerPalette.selection;
      context.lineWidth = 1.5;
      context.strokeRect(
        transform.originX + viewBox.x * transform.scale,
        transform.originY + viewBox.y * transform.scale,
        viewBox.width * transform.scale,
        viewBox.height * transform.scale,
      );
    });

    return () => cancelAnimationFrame(frame);
  }, [commands, projection, fullView, viewBox, width, height]);

  const centreOnEvent = (event: PointerEvent<HTMLCanvasElement>): void => {
    const bounds = event.currentTarget.getBoundingClientRect();

    // Through the same transform the minimap is drawn with, so a click lands where it looks
    // like it lands even though the plan is letterboxed inside this box.
    const point = elementToSurface(
      { x: event.clientX - bounds.left, y: event.clientY - bounds.top },
      surfaceTransformOf(bounds.width, bounds.height, fullView),
    );

    onCentreOn(point.x, point.y);
  };

  return (
    <Box
      sx={{
        ...floatingSlabSx,
        right: 12,
        bottom: 12,
        // Opaque, unlike the other floating slabs: this one holds a drawing of its own, and a
        // second plan showing faintly through it is unreadable.
        backgroundColor: editorTheme.surface,
        backdropFilter: 'none',
        overflow: 'hidden',
      }}
    >
      <canvas
        ref={canvasRef}
        aria-label={String(t('floorplan-editor-minimap'))}
        style={{ width: `${width}px`, height: `${height}px`, display: 'block', cursor: 'pointer', touchAction: 'none' }}
        onPointerDown={(event): void => {
          // Captured, so a drag that leaves the minimap keeps panning instead of stopping at
          // the edge — which is exactly the gesture for crossing a plan quickly.
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragging(true);
          centreOnEvent(event);
        }}
        onPointerMove={(event): void => {
          if (dragging) {
            centreOnEvent(event);
          }
        }}
        onPointerUp={(event): void => {
          event.currentTarget.releasePointerCapture(event.pointerId);
          setDragging(false);
        }}
        onPointerCancel={(): void => setDragging(false)}
      />
    </Box>
  );
};

export default Minimap;
